import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, X, Send, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input } from "@/components/Field";
import { CustomerSelect, ItemsEditor } from "@/components/admin/Editors";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

export default function Offers() {
  const { t, sl } = useI18n();
  const [params, setParams] = useSearchParams();
  const { items: offers, loading, reload: load } = useAdminData("/offers");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (params.get("new")) {
      const pre = JSON.parse(localStorage.getItem("jd_offer_prefill") || "{}");
      setEditing({ ...pre, items: [{ name: pre.title || "Leistung", qty: 1, price: 0 }], discount: 0, vat_rate: 0 });
      localStorage.removeItem("jd_offer_prefill");
      setParams({});
    }
  }, []); // eslint-disable-line

  const del = async (o) => {
    if (!window.confirm(`Angebot „${o.number}" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try { await api.delete(`/offers/${o.id}`); toast.success("Angebot gelöscht"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const save = async (form, sendIt) => {
    try {
      let res;
      if (form.id) res = await api.put(`/offers/${form.id}`, { ...form, status: sendIt ? "sent" : form.status });
      else res = await api.post("/offers", { ...form, status: sendIt ? "sent" : "draft" });
      toast.success("OK"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const respond = async (id, decision) => {
    try { await api.post(`/offers/${id}/respond`, { decision }); toast.success("OK"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">{t("admin.offers")}</h1>
        <button onClick={() => setEditing({ items: [{ name: "", qty: 1, price: 0 }], discount: 0, vat_rate: 0 })} className="btn-primary !py-2 !px-4 text-sm" data-testid="offer-new"><Plus size={16} /> {t("common.create")}</button>
      </div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground"><tr><th className="p-4">Nr.</th><th className="p-4">Kunde</th><th className="p-4">Titel</th><th className="p-4">{t("common.total")}</th><th className="p-4">{t("common.status")}</th><th className="p-4 text-right">{t("common.actions")}</th></tr></thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={6} /> : offers.map((o) => (
              <tr key={o.id} className="border-t border-border" data-testid={`offer-${o.id}`}>
                <td className="p-4 font-mono">{o.number}</td><td className="p-4">{o.customer_name}</td><td className="p-4">{o.title}</td>
                <td className="p-4 font-semibold">{(o.total || 0).toFixed(2)} €</td>
                <td className="p-4"><span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold capitalize">{sl(o.status)}</span></td>
                <td className="p-4"><div className="flex gap-1 justify-end">
                  <button onClick={() => setEditing(o)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`offer-edit-${o.id}`}>{t("common.edit")}</button>
                  {o.status === "sent" && <button onClick={() => respond(o.id, "accepted")} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`offer-accept-${o.id}`}><Check size={13} /></button>}
                  <button onClick={() => del(o)} className="btn-ghost !py-1.5 !px-2.5 text-xs text-red-500 hover:text-red-600" data-testid={`offer-delete-${o.id}`}><Trash2 size={13} /></button>
                </div></td>
              </tr>
            ))}
            {!loading && offers.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <OfferDrawer offer={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function OfferDrawer({ offer, onClose, onSave }) {
  const { t } = useI18n();
  const [f, setF] = useState({
    id: offer.id, title: offer.title || "", customer_id: offer.customer_id || "", customer_name: offer.customer_name || "",
    discount: offer.discount ?? 0, vat_rate: offer.vat_rate ?? 0, valid_until: (offer.valid_until || "").slice(0, 10),
    notes: offer.notes || "", status: offer.status || "draft",
  });
  const [items, setItems] = useState(offer.items || [{ name: "", qty: 1, price: 0 }]);
  const submit = (e, sendIt) => { e.preventDefault(); onSave({ ...f, items }, sendIt); };
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={(e) => submit(e, false)} className="relative w-full max-w-lg bg-background border-l border-border h-full overflow-y-auto p-6 space-y-4" data-testid="offer-drawer">
        <div className="flex items-center justify-between"><h2 className="font-display text-xl font-black">Angebot</h2><button type="button" onClick={onClose}><X size={20} /></button></div>
        <Field label="Kunde"><CustomerSelect testid="offer-customer" value={f.customer_id} onChange={(id, name) => setF({ ...f, customer_id: id, customer_name: name })} /></Field>
        <Field label="Titel"><Input testid="offer-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <div><span className="block text-sm font-medium mb-1.5">Positionen</span><ItemsEditor items={items} setItems={setItems} /></div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Rabatt €"><Input testid="offer-discount" type="number" value={f.discount} onChange={(e) => setF({ ...f, discount: Number(e.target.value) })} /></Field>
          <p className="text-xs text-muted-foreground">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p>
          <Field label="Gültig bis"><Input testid="offer-valid" type="date" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} /></Field>
        </div>
        <div className="flex gap-2 pt-2">
          <button className="btn-ghost flex-1" data-testid="offer-save">{t("common.save")}</button>
          <button type="button" onClick={(e) => submit(e, true)} className="btn-primary flex-1" data-testid="offer-send"><Send size={15} /> Senden</button>
        </div>
      </form>
    </div>
  );
}
